import React, { FC, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  GET_EMAIL_REPORT_SETTINGS,
  SEND_TEST_EMAIL_RECAP,
  UPDATE_EMAIL_REPORT_SETTINGS,
} from 'src/utils/gql/emailReportQueries';
import { GET_CUSTOM_REPORTS, GET_KPI_SECTIONS } from 'src/utils/gql/queries';
import { useGoogleAnalytics } from 'src/hooks/useGoogleAnalytics';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import palette from 'src/theme/palette';

type EmailReportSettings = {
  dailyRecapEnabled: boolean;
  weeklyRecapEnabled: boolean;
  monthlyRecapEnabled: boolean;
  advancedSource: string;
  kpiSectionId: string | null;
  customReportId: string | null;
  includeInventoryRestock: boolean;
  recipientEmails: string[];
  entitlements: {
    dailyAllowed: boolean;
    weeklyAllowed: boolean;
    monthlyAllowed: boolean;
    weeklyOncePerMonth: boolean;
    advancedSourcesAllowed: boolean;
    inventoryRestockAllowed: boolean;
  };
};

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const MAX_RECIPIENTS = 10;

const surfaceSx = {
  border: `1px solid ${palette.grey[200]}`,
  borderRadius: '16px',
  bgcolor: palette.common.white,
  overflow: 'hidden',
} as const;

const fieldSx = {
  bgcolor: palette.common.white,
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.grey[200],
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.grey[100],
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.primary.main,
    borderWidth: 1.5,
  },
  '& .MuiSelect-select': { py: 1.35 },
} as const;

function settingsSnapshot(s: EmailReportSettings): string {
  return JSON.stringify({
    dailyRecapEnabled: s.dailyRecapEnabled,
    weeklyRecapEnabled: s.weeklyRecapEnabled,
    monthlyRecapEnabled: s.monthlyRecapEnabled,
    advancedSource: s.advancedSource,
    kpiSectionId: s.kpiSectionId,
    customReportId: s.customReportId,
    includeInventoryRestock: s.includeInventoryRestock,
    recipientEmails: s.recipientEmails,
  });
}

type ToggleRowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  hint?: React.ReactNode;
  dense?: boolean;
};

const ToggleRow: FC<ToggleRowProps> = ({
  icon,
  title,
  description,
  checked,
  disabled,
  onChange,
  hint,
  dense,
}) => (
  <Box
    sx={{
      px: dense ? 0 : { xs: 2, sm: 2.5 },
      py: dense ? 1.25 : 2,
      opacity: disabled ? 0.55 : 1,
      transition: 'background-color 120ms ease',
      '&:hover': {
        bgcolor: disabled ? 'transparent' : palette.action.hover,
      },
    }}
  >
    <Stack direction="row" spacing={1.75} alignItems="flex-start">
      <Box
        sx={{
          mt: 0.25,
          width: 36,
          height: 36,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          bgcolor: checked ? palette.primary.light : palette.grey[200],
          color: checked ? palette.secondary.light : palette.text.secondary,
          transition: 'background-color 160ms ease, color 160ms ease',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 15,
            color: palette.text.primary,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mt: 0.35,
            color: palette.text.secondary,
            lineHeight: 1.45,
            maxWidth: 460,
          }}
        >
          {description}
        </Typography>
        {hint}
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
        color="primary"
        sx={{ mt: 0.25 }}
      />
    </Stack>
  </Box>
);

export const EmailReportsSettings: FC = () => {
  const snackbar = useSnackbar();
  const { trackCustomEvent } = useGoogleAnalytics();
  const { data, loading, error } = useQuery(GET_EMAIL_REPORT_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: kpiData } = useQuery(GET_KPI_SECTIONS, {
    fetchPolicy: 'cache-first',
  });
  const { data: reportsData } = useQuery(GET_CUSTOM_REPORTS, {
    fetchPolicy: 'cache-first',
  });

  const [updateSettings, { loading: saving }] = useMutation(
    UPDATE_EMAIL_REPORT_SETTINGS,
  );
  // Local/dev QA only — hidden in production builds (scripts remain for manual tests).
  const showLocalTestEmail = process.env.NODE_ENV !== 'production';
  const [sendTestEmail, { loading: sendingTest }] = useMutation(
    SEND_TEST_EMAIL_RECAP,
  );
  const [testRecapType, setTestRecapType] = useState<'daily' | 'weekly' | 'monthly'>(
    'daily',
  );

  const remote: EmailReportSettings | undefined =
    data?.getEmailReportSettings;

  const [saved, setSaved] = useState<EmailReportSettings | null>(null);
  const [local, setLocal] = useState<EmailReportSettings | null>(null);
  const [recipientDraft, setRecipientDraft] = useState('');

  useEffect(() => {
    if (!remote) return;
    setSaved(remote);
    setLocal(remote);
    const e = remote.entitlements;
    if (e.dailyAllowed) setTestRecapType('daily');
    else if (e.weeklyAllowed) setTestRecapType('weekly');
    else if (e.monthlyAllowed) setTestRecapType('monthly');
  }, [remote]);

  const kpiSections = useMemo(
    () => (kpiData?.getKpiSections ?? []) as Array<{ id: string; name: string }>,
    [kpiData],
  );
  const customReports = useMemo(
    () =>
      (reportsData?.getCustomReports ?? []) as Array<{
        id: string;
        name: string;
      }>,
    [reportsData],
  );

  const dirty =
    Boolean(local && saved) &&
    settingsSnapshot(local!) !== settingsSnapshot(saved!);

  const patchLocal = (patch: Partial<EmailReportSettings>) => {
    setLocal((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const savePreferences = async () => {
    if (!local || !saved) return;
    if (local.recipientEmails.length === 0) {
      snackbar('Keep at least one recipient email', 'error');
      return;
    }
    try {
      const result = await updateSettings({
        variables: {
          settings: {
            dailyRecapEnabled: local.dailyRecapEnabled,
            weeklyRecapEnabled: local.weeklyRecapEnabled,
            monthlyRecapEnabled: local.monthlyRecapEnabled,
            advancedSource: local.advancedSource,
            kpiSectionId: local.kpiSectionId,
            customReportId: local.customReportId,
            includeInventoryRestock: local.includeInventoryRestock,
            recipientEmails: local.recipientEmails,
          },
        },
      });
      const updated = result.data?.updateEmailReportSettings as
        | EmailReportSettings
        | undefined;
      if (!updated) {
        snackbar('Failed to save Email Reports preferences', 'error');
        return;
      }
      setSaved(updated);
      setLocal(updated);

      if (updated.dailyRecapEnabled !== saved.dailyRecapEnabled) {
        trackCustomEvent(
          updated.dailyRecapEnabled
            ? 'daily_recap_enabled'
            : 'daily_recap_disabled',
          { recap_type: 'daily' },
        );
      }
      if (updated.weeklyRecapEnabled !== saved.weeklyRecapEnabled) {
        trackCustomEvent(
          updated.weeklyRecapEnabled
            ? 'weekly_recap_enabled'
            : 'weekly_recap_disabled',
          { recap_type: 'weekly' },
        );
      }
      if (updated.monthlyRecapEnabled !== saved.monthlyRecapEnabled) {
        trackCustomEvent(
          updated.monthlyRecapEnabled
            ? 'monthly_recap_enabled'
            : 'monthly_recap_disabled',
          { recap_type: 'monthly' },
        );
      }

      snackbar('Email Reports preferences saved', 'success');
    } catch (e) {
      snackbar(
        e instanceof Error ? e.message : 'Failed to save Email Reports',
        'error',
      );
    }
  };

  const discardChanges = () => {
    if (!saved) return;
    setLocal(saved);
    setRecipientDraft('');
  };

  const sendTest = async () => {
    if (!local) return;
    if (local.recipientEmails.length === 0) {
      snackbar('Add at least one recipient email', 'error');
      return;
    }
    try {
      const result = await sendTestEmail({
        variables: {
          recapType: testRecapType,
          settings: {
            dailyRecapEnabled: local.dailyRecapEnabled,
            weeklyRecapEnabled: local.weeklyRecapEnabled,
            monthlyRecapEnabled: local.monthlyRecapEnabled,
            advancedSource: local.advancedSource,
            kpiSectionId: local.kpiSectionId,
            customReportId: local.customReportId,
            includeInventoryRestock: local.includeInventoryRestock,
            recipientEmails: local.recipientEmails,
          },
        },
      });
      const payload = result.data?.sendTestEmailRecap as
        | {
            ok: boolean;
            recapType: string;
            recipients: string[];
            subject: string;
            warnings: string[];
          }
        | undefined;
      if (!payload?.ok) {
        snackbar('Failed to send test email', 'error');
        return;
      }
      const warn =
        payload.warnings?.length > 0
          ? ` (${payload.warnings[0]})`
          : '';
      snackbar(
        `Test ${payload.recapType} email sent to ${payload.recipients.join(', ')}${warn}`,
        payload.warnings?.length ? 'warning' : 'success',
      );
      trackCustomEvent('recap_test_sent', {
        recap_type: payload.recapType,
      });
    } catch (e) {
      snackbar(
        e instanceof Error ? e.message : 'Failed to send test email',
        'error',
      );
    }
  };

  const addRecipient = () => {
    if (!local) return;
    const email = recipientDraft.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      snackbar('Enter a valid email address', 'error');
      return;
    }
    if (local.recipientEmails.includes(email)) {
      setRecipientDraft('');
      return;
    }
    if (local.recipientEmails.length >= MAX_RECIPIENTS) {
      snackbar(`You can add up to ${MAX_RECIPIENTS} emails`, 'error');
      return;
    }
    setRecipientDraft('');
    patchLocal({ recipientEmails: [...local.recipientEmails, email] });
  };

  const removeRecipient = (email: string) => {
    if (!local) return;
    if (local.recipientEmails.length <= 1) {
      snackbar('Keep at least one recipient email', 'error');
      return;
    }
    patchLocal({
      recipientEmails: local.recipientEmails.filter((e) => e !== email),
    });
  };

  if (loading && !local) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !local) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        Unable to load Email Reports settings. Please try again later.
      </Alert>
    );
  }

  const e = local.entitlements;

  return (
    <Box sx={{ maxWidth: 680, pb: 4 }}>
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: palette.text.primary,
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}
        >
          Email Reports
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: palette.text.secondary, maxWidth: 520, lineHeight: 1.5 }}
        >
          Concise performance recaps in your store timezone and currency.
          Choose who receives them and how often.
        </Typography>
      </Stack>

      <Stack spacing={2.25}>
        {/* Recipients */}
        <Box sx={surfaceSx}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.25, pb: 1.5 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: palette.secondary.main,
                color: palette.secondary.light,
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                Recipients
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Up to {MAX_RECIPIENTS} emails for Daily, Weekly, and Monthly
                reports.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.25 }}>
            <Box
              sx={{
                border: `1px solid ${palette.grey[200]}`,
                borderRadius: '12px',
                bgcolor: '#FAFBFC',
                p: 1.25,
                minHeight: 52,
              }}
            >
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{ mb: local.recipientEmails.length ? 1.25 : 0 }}
              >
                {local.recipientEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    size="small"
                    onDelete={
                      local.recipientEmails.length > 1
                        ? () => removeRecipient(email)
                        : undefined
                    }
                    disabled={saving}
                    sx={{
                      bgcolor: palette.primary.light,
                      color: palette.text.primary,
                      fontWeight: 500,
                      '& .MuiChip-deleteIcon': {
                        color: palette.secondary.light,
                        '&:hover': { color: palette.text.primary },
                      },
                    }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add email…"
                  value={recipientDraft}
                  disabled={saving}
                  onChange={(ev) => setRecipientDraft(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      ev.preventDefault();
                      addRecipient();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: palette.common.white,
                      borderRadius: '10px',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRecipient}
                  disabled={saving || !recipientDraft.trim()}
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: 96,
                    borderRadius: '10px',
                    borderColor: palette.grey[200],
                    color: palette.text.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Cadence */}
        <Box sx={surfaceSx}>
          <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.25, pb: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: palette.text.secondary,
              }}
            >
              Schedule
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.35, color: palette.text.secondary }}
            >
              All sends around 09:00 in your store timezone.
            </Typography>
          </Box>

          <ToggleRow
            icon={<TodayOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Daily Recap"
            description="Yesterday’s performance with up to 3 things to watch."
            checked={local.dailyRecapEnabled}
            disabled={!e.dailyAllowed || saving}
            onChange={(checked) => patchLocal({ dailyRecapEnabled: checked })}
            hint={
              !e.dailyAllowed ? (
                <Alert
                  severity="info"
                  sx={{ mt: 1.25, borderRadius: '10px', py: 0 }}
                >
                  Available on Pro and Advanced.
                </Alert>
              ) : null
            }
          />
          <Divider sx={{ borderColor: palette.grey[200], mx: { xs: 2, sm: 2.5 } }} />
          <ToggleRow
            icon={<DateRangeOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Weekly Business Review"
            description="Previous 7 days, WoW comparisons, and key takeaways. Sent every week."
            checked={local.weeklyRecapEnabled}
            disabled={!e.weeklyAllowed || saving}
            onChange={(checked) => patchLocal({ weeklyRecapEnabled: checked })}
            hint={
              !e.weeklyAllowed ? (
                <Alert
                  severity="info"
                  sx={{ mt: 1.25, borderRadius: '10px', py: 0 }}
                >
                  Not available on your current plan.
                </Alert>
              ) : null
            }
          />
          <Divider sx={{ borderColor: palette.grey[200], mx: { xs: 2, sm: 2.5 } }} />
          <ToggleRow
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Monthly Recap"
            description="Previous calendar month vs the same month last year (YoY)."
            checked={local.monthlyRecapEnabled}
            disabled={!e.monthlyAllowed || saving}
            onChange={(checked) => patchLocal({ monthlyRecapEnabled: checked })}
            hint={
              !e.monthlyAllowed ? (
                <Alert
                  severity="info"
                  sx={{ mt: 1.25, borderRadius: '10px', py: 0 }}
                >
                  Available on Starter and higher.
                </Alert>
              ) : null
            }
          />
        </Box>

        {/* Advanced */}
        {e.advancedSourcesAllowed && (
          <Box sx={surfaceSx}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.25, pb: 1.5 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: palette.secondary.main,
                  color: palette.secondary.light,
                }}
              >
                <TuneOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
                  Advanced source
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Finance by default, or use a saved KPI section / custom
                  report.
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2.25 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.75,
                  color: palette.text.secondary,
                  fontWeight: 600,
                }}
              >
                Source
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value={local.advancedSource || 'finance'}
                  displayEmpty
                  disabled={saving}
                  onChange={(ev) =>
                    patchLocal({ advancedSource: String(ev.target.value) })
                  }
                  sx={fieldSx}
                >
                  <MenuItem value="finance">Finance P&L</MenuItem>
                  <MenuItem value="kpi_section">KPI section</MenuItem>
                  <MenuItem value="custom_report">Custom report</MenuItem>
                </Select>
              </FormControl>

              {local.advancedSource === 'kpi_section' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      color: palette.text.secondary,
                      fontWeight: 600,
                    }}
                  >
                    KPI section
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                      value={local.kpiSectionId || ''}
                      displayEmpty
                      disabled={saving}
                      onChange={(ev) =>
                        patchLocal({
                          kpiSectionId: String(ev.target.value) || null,
                        })
                      }
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        <em>Select a section</em>
                      </MenuItem>
                      {kpiSections.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {local.advancedSource === 'custom_report' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      color: palette.text.secondary,
                      fontWeight: 600,
                    }}
                  >
                    Custom report
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <Select
                      value={local.customReportId || ''}
                      displayEmpty
                      disabled={saving}
                      onChange={(ev) =>
                        patchLocal({
                          customReportId: String(ev.target.value) || null,
                        })
                      }
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        <em>Select a report</em>
                      </MenuItem>
                      {customReports.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {e.inventoryRestockAllowed && (
                <>
                  <Divider sx={{ borderColor: palette.grey[200], my: 1 }} />
                  <ToggleRow
                    dense
                    icon={<Inventory2OutlinedIcon sx={{ fontSize: 20 }} />}
                    title="Include inventory to restock"
                    description="Add low-stock SKUs flagged by Kipify to your recap email."
                    checked={local.includeInventoryRestock}
                    disabled={saving}
                    onChange={(checked) =>
                      patchLocal({ includeInventoryRestock: checked })
                    }
                  />
                </>
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.25,
            pt: 0.5,
            flexWrap: 'wrap',
            // Settings TabContent styles every contained button with mt/mb for forms;
            // neutralize here so action buttons stay on one line.
            '& .MuiButton-root': {
              marginTop: '0 !important',
              marginBottom: '0 !important',
              height: '44px !important',
              minHeight: '44px !important',
              maxHeight: '44px !important',
              maxWidth: 'none !important',
              alignSelf: 'center !important',
              fontSize: '15px !important',
              fontWeight: '700 !important',
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              lineHeight: '44px !important',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {showLocalTestEmail && (
              <>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={testRecapType}
                    disabled={saving || sendingTest}
                    onChange={(ev) =>
                      setTestRecapType(
                        ev.target.value as 'daily' | 'weekly' | 'monthly',
                      )
                    }
                    sx={{
                      ...fieldSx,
                      height: 44,
                      '& .MuiSelect-select': { py: 1.1 },
                    }}
                  >
                    <MenuItem value="daily" disabled={!e.dailyAllowed}>
                      Daily
                    </MenuItem>
                    <MenuItem value="weekly" disabled={!e.weeklyAllowed}>
                      Weekly
                    </MenuItem>
                    <MenuItem value="monthly" disabled={!e.monthlyAllowed}>
                      Monthly
                    </MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => void sendTest()}
                  disabled={
                    saving || sendingTest || local.recipientEmails.length === 0
                  }
                  sx={{
                    textTransform: 'none',
                    boxSizing: 'border-box',
                    height: 44,
                    minHeight: 44,
                    px: 2.5,
                    borderRadius: '10px',
                    borderColor: `${palette.grey[200]} !important`,
                    color: palette.text.primary,
                    bgcolor: palette.common.white,
                    '&:hover': {
                      borderColor: `${palette.grey[100]} !important`,
                      bgcolor: palette.action.hover,
                    },
                  }}
                >
                  {sendingTest ? 'Sending…' : 'Send test email'}
                </Button>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {dirty && (
              <Button
                variant="outlined"
                size="medium"
                onClick={discardChanges}
                disabled={saving || sendingTest}
                sx={{
                  textTransform: 'none',
                  boxSizing: 'border-box',
                  height: 44,
                  minHeight: 44,
                  maxHeight: 44,
                  py: 0,
                  px: 2.5,
                  m: 0,
                  lineHeight: '44px',
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: '10px',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: `${palette.grey[200]} !important`,
                  color: palette.text.primary,
                  bgcolor: palette.common.white,
                  '&:hover': {
                    borderColor: `${palette.grey[100]} !important`,
                    bgcolor: palette.action.hover,
                  },
                }}
              >
                Discard
              </Button>
            )}
            <Button
              variant="contained"
              size="medium"
              onClick={() => void savePreferences()}
              disabled={saving || sendingTest || !dirty}
              sx={{
                textTransform: 'none',
                boxSizing: 'border-box',
                height: 44,
                minHeight: 44,
                maxHeight: 44,
                py: 0,
                px: 2.5,
                m: 0,
                lineHeight: '44px',
                fontSize: 15,
                fontWeight: 700,
                borderRadius: '10px',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'transparent',
                boxShadow: 'none',
                bgcolor: palette.primary.main,
                color: palette.primary.contrastText,
                '&:hover': {
                  bgcolor: 'rgba(30, 237, 159, 0.88)',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: palette.grey[200],
                  color: palette.text.disabled,
                  borderColor: 'transparent',
                },
              }}
            >
              {saving ? 'Saving…' : 'Save preferences'}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default EmailReportsSettings;
